import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import type { ModelSelectionValue } from 'pages/agents/components/agent-model-picker';
import {
  buildAgentConfigPayload,
  capabilitiesFromSectionState,
  getAgentConfigValues,
  type AgentConfigValues,
} from 'pages/agents/utils/agent-config-payload';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { BASE_PROMPT_SYNC_NOTICE, resolveSyncedBasePrompt } from 'utils/agent-default-prompt';
import type { ITool } from '../agent-chat-module/services/gpt-chat.service';
import type { Agent } from '../agent-chat-module/types/agent.service.type';
import { useUpdateAgentAIConfigurations } from './use-agents';
import { useDebouncedAgentSave } from './use-debounced-agent-save';

/**
 * Agent configuration edited outside the configuration page (the chat settings drawer),
 * saved with the same update-ai-configurations payload. All sections share one state,
 * so saving one section never sends an old value for another.
 */
export function useAgentConfigSettings(agent: Agent, projectKey: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const userId = useCommonStore((state) => state.user?.id);
  const { mutateAsync: updateAgentAIConfigurations } = useUpdateAgentAIConfigurations();
  const [values, setValues] = useState(() => getAgentConfigValues(agent));

  const saveLater = useDebouncedAgentSave(async (nextValues: AgentConfigValues) => {
    try {
      await updateAgentAIConfigurations(
        buildAgentConfigPayload(agent, projectKey, nextValues, userId)
      );
    } catch (error) {
      // Like the configuration page, go back to what is saved.
      setValues(getAgentConfigValues(agent));
      throw error;
    }
  });

  const updateValues = (nextValues: AgentConfigValues) => {
    setValues(nextValues);
    saveLater(nextValues);
  };

  // Like the configuration page, switching a capability re-syncs a default base prompt.
  const updateSection = (nextValues: AgentConfigValues, key: string, checked: boolean) => {
    const sectionState = { ...nextValues.sectionState, [key]: checked };
    const basePrompt = resolveSyncedBasePrompt({
      proposedBasePrompt: nextValues.basePrompt,
      prevCapabilities: capabilitiesFromSectionState(nextValues.sectionState),
      nextCapabilities: capabilitiesFromSectionState(sectionState),
    });

    if (basePrompt !== nextValues.basePrompt) {
      toast({
        variant: 'info',
        title: t('BASE_PROMPT_UPDATED', { defaultValue: BASE_PROMPT_SYNC_NOTICE.title }),
        description: t('BASE_PROMPT_UPDATED_DESCRIPTION', {
          defaultValue: BASE_PROMPT_SYNC_NOTICE.description,
        }),
      });
    }

    updateValues({ ...nextValues, sectionState, basePrompt });
  };

  const setSectionEnabled = (key: string, checked: boolean) => {
    updateSection(values, key, checked);
  };

  const addFolder = (folderId: string) => {
    if (values.folderIds.includes(folderId)) return;
    updateValues({ ...values, folderIds: [...values.folderIds, folderId] });
  };

  const removeFolder = (folderId: string) => {
    updateValues({ ...values, folderIds: values.folderIds.filter((id) => id !== folderId) });
  };

  // Adding a tool also switches Tools on, as on the configuration page.
  const addTool = (tool: ITool) => {
    if (values.toolIds.includes(tool.id)) return;
    updateSection({ ...values, toolIds: [...values.toolIds, tool.id] }, 'tools', true);
  };

  const removeTool = (toolId: string) => {
    updateValues({ ...values, toolIds: values.toolIds.filter((id) => id !== toolId) });
  };

  // Adding a skill also switches Skills on, as on the configuration page.
  const addSkill = (skillId: string) => {
    if (values.skillIds.includes(skillId)) return;
    updateSection({ ...values, skillIds: [...values.skillIds, skillId] }, 'skills', true);
  };

  const removeSkill = (skillId: string) => {
    updateValues({ ...values, skillIds: values.skillIds.filter((id) => id !== skillId) });
  };

  // The picker sends the model's own temperature and max tokens, as on the configuration page.
  const setModel = (model: ModelSelectionValue) => {
    updateValues({
      ...values,
      llmModel: model.modelName,
      llmProvider: model.provider,
      llmModelId: model.modelId || '',
      isBlocksProvider: model.isBlocksProvider,
      temperature: model.temperature,
      maxTokens: model.maxTokens,
    });
  };

  return {
    values,
    setModel,
    setSectionEnabled,
    addFolder,
    removeFolder,
    addTool,
    removeTool,
    addSkill,
    removeSkill,
  };
}
